/**
 * collabFormShared.js
 * Single source of truth for Collab Form state, legacy validation rules,
 * choices, and payload normalization.
 */

export const TYPES = [
  ['1', 'Restaurantes y Vida Nocturna'],
  ['2', 'Retail'],
  ['3', 'Experiencias'],
  ['4', 'Eventos'],
  ['5', 'Clubs'],
  ['6', 'Hoteles'],
  ['7', 'Fitness'],
  ['8', 'Bienestar y Belleza'],
  ['9', 'Viajes'],
  ['10', 'Kits de PR'],
  ['11', 'Otro'],
];

export const FOLLOWER_REQUIREMENTS = [
  ['1', 'Sin requisitos'],
  ['2', 'Friends of the house'],
  ['3', '3000 - 15000'],
  ['4', '+15,000'],
];

export const STATIC_EXCHANGE_OPTIONS = [
  { id: 1, code: '1', name: 'Post' },
  { id: 2, code: '2', name: 'Story' },
  { id: 3, code: '3', name: 'Reel' },
];

export const STATIC_TARGET_OPTIONS = [
  { id: 1, code: '1', name: 'Lifestyle' },
  { id: 2, code: '2', name: 'Fashion' },
  { id: 3, code: '3', name: 'Beauty' },
  { id: 4, code: '4', name: 'Food' },
  { id: 5, code: '5', name: 'Wellness' },
  { id: 6, code: '6', name: 'Entertainment' },
  { id: 7, code: '7', name: 'Travel' },
  { id: 8, code: '8', name: 'Sports' },
  { id: 9, code: '9', name: 'Tech' },
];

export const DEFAULT_COUNTRIES = [
  ['11', 'Panamá'],
  ['12', 'Colombia'],
  ['14', 'México'],
  ['15', 'Costa Rica'],
  ['16', 'El Salvador'],
  ['17', 'Perú'],
  ['18', 'Brasil'],
  ['19', 'Argentina'],
];

export const FORM_STEPS = [
  {
    title: 'Información principal',
    subtitle: 'Define la oferta, el valor, la audiencia temporal y el tipo de colaboración.',
  },
  {
    title: 'Audiencia y entregables',
    subtitle: 'Indica quién puede aplicar y qué contenido esperas recibir.',
  },
  {
    title: 'Assets y condiciones',
    subtitle: 'Añade imágenes, reglas y cualquier detalle importante antes de publicar.',
  },
];

export const getLocalizedTypes = (t) => {
  if (!t) return TYPES;
  return TYPES.map(([code, def]) => [code, t(`form_shared.types.${code}`, def)]);
};

export const getLocalizedFollowerRequirements = (t) => {
  if (!t) return FOLLOWER_REQUIREMENTS;
  return FOLLOWER_REQUIREMENTS.map(([code, def]) => [code, t(`form_shared.follower_requirements.${code}`, def)]);
};

export const getLocalizedFormSteps = (t) => {
  if (!t) return FORM_STEPS;
  return [
    {
      title: t('form_shared.steps.step1_title', FORM_STEPS[0].title),
      subtitle: t('form_shared.steps.step1_subtitle', FORM_STEPS[0].subtitle),
    },
    {
      title: t('form_shared.steps.step2_title', FORM_STEPS[1].title),
      subtitle: t('form_shared.steps.step2_subtitle', FORM_STEPS[1].subtitle),
    },
    {
      title: t('form_shared.steps.step3_title', FORM_STEPS[2].title),
      subtitle: t('form_shared.steps.step3_subtitle', FORM_STEPS[2].subtitle),
    },
  ];
};

export const parseDateOnly = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const formatDateInputValue = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') {
    return dateValue.slice(0, 10);
  }
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return dateValue.toISOString().slice(0, 10);
  }
  return '';
};

export const getFutureDate = (daysToAdd) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

export const emptyForm = (mode = 'exchange') => {
  const from = getFutureDate(1);
  const to = getFutureDate(16);
  const isPaid = mode === 'paid';

  return {
    title: '',
    type: '',
    value: '',
    compensation_mode: isPaid ? 'paid' : 'exchange',
    cash_currency: 'USD',
    sponsor_amount: '',
    quantity: 1,
    country: '',
    followers_requirements: '1',
    from_date: from.toISOString().slice(0, 10),
    to_date: to.toISOString().slice(0, 10),
    description: '',
    exchange_options: [],
    deliverables_counts: {},
    deliverables_mode: 'defined',
    target_options: [],
    terms_and_conditions: '',
    sponsor_this_offer: isPaid,
    featured_image: null,
    additional_images: [],
    add_time_range: false,
  };
};

export const isRichTextEmpty = (content) => {
  if (!content) return true;
  const textContent = String(content).replace(/<[^>]*>/g, '').trim();
  return textContent === '';
};

export const hasPositiveDeliverableCount = (counts = {}) => (
  Object.values(counts || {}).some((count) => Number.parseInt(count, 10) > 0)
);

export const buildDefinedDeliverablesCounts = (selectedOptions = [], currentCounts = {}) => (
  selectedOptions.reduce((counts, optionId) => {
    const numericCount = Number.parseInt(currentCounts[optionId], 10);
    counts[optionId] = Number.isNaN(numericCount) ? 1 : Math.max(numericCount, 0);
    return counts;
  }, {})
);

export const validateCollabStep = ({
  step,
  formData,
  mode = 'exchange',
  maxCreators = 0,
  t = (k, def) => def || k,
  hasFeaturedPreview = false,
}) => {
  const errors = {};
  const isPaid = mode === 'paid' || formData.compensation_mode === 'paid';

  if (step === 1) {
    if (!formData.title || !formData.title.trim()) {
      errors.title = t('create.errors.title_req');
    }
    if (!formData.type) {
      errors.type = t('create.errors.type_req');
    }
    if (!isPaid && !formData.value) {
      errors.value = t('create.errors.budget_req');
    }
    if (isPaid && (!formData.sponsor_amount || Number(formData.sponsor_amount) <= 0)) {
      errors.sponsor_amount = t('create.errors.paid_amount_req', 'Set a positive payment per creator.');
    }

    const qty = Number(formData.quantity);
    if (!formData.quantity || Number.isNaN(qty) || qty <= 0) {
      errors.quantity = t('create.errors.quantity_req', 'Indica un número válido de creadores (mínimo 1)');
    } else if (maxCreators > 0 && qty > maxCreators) {
      errors.quantity = t('create.errors.quantity_max', {
        count: maxCreators,
        defaultValue: `Tu plan permite hasta ${maxCreators} creadores por collab.`,
      });
    }

    if (!formData.country) {
      errors.country = t('create.errors.country_req');
    }

    if (isRichTextEmpty(formData.description)) {
      errors.description = t('create.errors.desc_req');
    }

    const parsedFrom = parseDateOnly(formData.from_date);
    const parsedTo = parseDateOnly(formData.to_date);

    if (!parsedFrom || !parsedTo) {
      errors.dates = t('create.errors.dates_req');
    } else if (parsedTo <= parsedFrom) {
      errors.dates = t('create.errors.dates_order');
    } else {
      const maxEndDate = new Date(parsedFrom);
      maxEndDate.setDate(parsedFrom.getDate() + 30);
      if (parsedTo > maxEndDate) {
        errors.dates = t('create.errors.dates_max');
      }
    }
  }

  if (step === 2) {
    if (formData.deliverables_mode === 'defined') {
      if (!formData.exchange_options || formData.exchange_options.length === 0) {
        errors.exchange_options = t('create.errors.exchange_req');
      }
      const definedCounts = buildDefinedDeliverablesCounts(formData.exchange_options, formData.deliverables_counts);
      if (!hasPositiveDeliverableCount(definedCounts)) {
        errors.deliverables_counts = t(
          'create.errors.deliverables_count_req',
          'Add at least one deliverable quantity greater than zero.'
        );
      }
    }
    if (!formData.target_options || formData.target_options.length === 0) {
      errors.target_options = t('create.errors.target_req');
    }
  }

  if (step === 3) {
    if (!formData.featured_image && !hasFeaturedPreview) {
      errors.featured_image = t('create.errors.image_req');
    }
    if (isRichTextEmpty(formData.terms_and_conditions)) {
      errors.terms_and_conditions = t('create.errors.terms_req');
    }
    if (!isPaid && formData.sponsor_this_offer && (!formData.sponsor_amount || Number(formData.sponsor_amount) <= 0)) {
      errors.sponsor_amount = t('create.errors.sponsor_amount_req');
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateAllCollabSteps = ({
  formData,
  mode = 'exchange',
  maxCreators = 0,
  t = (k, def) => def || k,
  hasFeaturedPreview = false,
}) => {
  const allErrors = {};
  let firstInvalidStep = null;

  for (let s = 1; s <= 3; s += 1) {
    const { isValid, errors } = validateCollabStep({
      step: s,
      formData,
      mode,
      maxCreators,
      t,
      hasFeaturedPreview,
    });
    if (!isValid) {
      Object.assign(allErrors, errors);
      if (firstInvalidStep === null) {
        firstInvalidStep = s;
      }
    }
  }

  return {
    isValid: Object.keys(allErrors).length === 0,
    errors: allErrors,
    firstInvalidStep,
  };
};

export const normalizeCollabPayload = (formData, mode = 'exchange') => {
  const isPaid = mode === 'paid' || formData.compensation_mode === 'paid';
  const sponsorThisOffer = isPaid || Boolean(formData.sponsor_this_offer);
  const compMode = isPaid ? 'paid' : (formData.sponsor_this_offer ? 'hybrid' : 'exchange');

  const normalized = {
    ...formData,
    add_booking: false,
    compensation_mode: compMode,
    sponsor_this_offer: sponsorThisOffer,
    deliverables_counts: formData.deliverables_mode === 'defined'
      ? buildDefinedDeliverablesCounts(formData.exchange_options, formData.deliverables_counts)
      : {},
  };

  const payload = new FormData();
  Object.keys(normalized).forEach((key) => {
    if (key === 'additional_images') {
      if (Array.isArray(normalized.additional_images)) {
        normalized.additional_images.forEach((file) => {
          if (file instanceof File || file instanceof Blob) {
            payload.append('additional_images', file);
          }
        });
      }
    } else if (key === 'featured_image') {
      if (normalized.featured_image instanceof File || normalized.featured_image instanceof Blob) {
        payload.append('featured_image', normalized.featured_image);
      }
    } else if (Array.isArray(normalized[key])) {
      normalized[key].forEach((item) => payload.append(key, item));
    } else if (key === 'deliverables_counts') {
      payload.append(key, JSON.stringify(normalized[key]));
    } else if (normalized[key] !== null && normalized[key] !== undefined) {
      if (normalized[key] instanceof Date) {
        payload.append(key, normalized[key].toISOString().split('T')[0]);
      } else {
        payload.append(key, normalized[key]);
      }
    }
  });

  return { normalized, payload };
};
